import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, CheckCircle, MapPin } from "@phosphor-icons/react";

function AddressBlock({ label, addr, name }) {
    if (!addr || !addr.line1) return <p className="text-sm text-rose-600">Address missing</p>;
    return (
        <div className="text-sm bg-slate-50 border border-slate-200 rounded-md p-3">
            <p className="overline text-[10px] text-slate-500 mb-1">{label}</p>
            <p className="font-medium text-slate-900">{name}</p>
            <p className="text-slate-700">{addr.line1}{addr.line2 ? ` ${addr.line2}` : ""}</p>
            <p className="text-slate-700">{addr.city}, {addr.state} {addr.postal_code}</p>
            {addr.verified && (
                <Badge variant="outline" className="mt-2 gap-1 bg-emerald-50 text-emerald-700 border-emerald-200">
                    <CheckCircle size={11} weight="bold" /> USPS verified
                </Badge>
            )}
        </div>
    );
}

export default function MailLetterDialog({ open, onOpenChange, caseData, aiOutput, userName, onSent }) {
    const [postage, setPostage] = useState("standard");
    const [options, setOptions] = useState([]);
    const [environment, setEnvironment] = useState("test");
    const [markup, setMarkup] = useState(0);
    const [balanceCents, setBalanceCents] = useState(0);
    const [payWith, setPayWith] = useState("auto");  // "auto" | "credits" | "card"
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (!open) return;
        setPayWith("auto");
        api.get("/lob/pricing").then((r) => {
            setOptions(r.data.options || []);
            setEnvironment(r.data.environment || "test");
            setMarkup(r.data.markup_usd || 0);
        }).catch(() => {});
        api.get("/credits/balance").then((r) => setBalanceCents(r.data.credits_cents || 0)).catch(() => {});
    }, [open]);

    const currentOpt = options.find((o) => o.key === postage);
    const chargeUsd = currentOpt ? (currentOpt.charge_usd ?? currentOpt.estimate_usd) : 0;
    const chargeCents = Math.round(chargeUsd * 100);
    const canUseCredits = balanceCents >= chargeCents && chargeCents > 0;
    const effectiveMethod = payWith === "card"
        ? "card"
        : (payWith === "credits" || (payWith === "auto" && canUseCredits)) ? "credits" : "card";

    const send = async () => {
        if (!caseData || !aiOutput) return;
        setSending(true);
        try {
            const { data } = await api.post("/lob/mail-checkout", {
                case_id: caseData.id,
                ai_output_id: aiOutput.id,
                postage_class: postage,
                origin_url: window.location.origin,
                pay_with: payWith,
            });
            if (data.paid_with_credits) {
                toast.success(`Letter mailed — $${(data.charged_cents / 100).toFixed(2)} debited. Balance: $${(data.new_balance_cents / 100).toFixed(2)}`);
                onOpenChange(false);
                if (onSent) onSent(data.mailing);
                return;
            }
            toast.info("Redirecting to secure checkout…");
            try {
                sessionStorage.setItem("lienlift_letter_pay", JSON.stringify({
                    session_id: data.session_id,
                    case_id: caseData.id,
                    ai_output_id: aiOutput.id,
                }));
            } catch (_) { /* sessionStorage unavailable — non-critical */ }
            window.location.href = data.url;
            if (onSent) onSent();
        } catch (err) {
            toast.error(formatApiError(err));
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl" data-testid="mail-letter-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <PaperPlaneTilt size={22} weight="duotone" className="text-[#0F1E3D]" />
                        Mail this letter via USPS
                    </DialogTitle>
                    <DialogDescription>
                        LienLift will print and mail the letter for you. The debtor address is used as the return address.
                    </DialogDescription>
                </DialogHeader>

                {environment === "test" && (
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-2.5 text-xs text-amber-900" data-testid="lob-sandbox-notice">
                        <strong>Sandbox mode:</strong> letters are created in Lob&apos;s test environment — no real mail is sent and no charges are incurred.
                    </div>
                )}

                {canUseCredits && (
                    <div className="border border-slate-200 rounded-md p-3 space-y-2" data-testid="mail-payment-method">
                        <div className="flex items-center justify-between text-xs">
                            <span className="overline text-slate-500">Payment method</span>
                            <span className="text-emerald-700">Balance: <strong className="font-mono">${(balanceCents / 100).toFixed(2)}</strong></span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                onClick={() => setPayWith("credits")}
                                data-testid="paywith-credits"
                                className={`text-left border rounded-md p-3 transition-colors ${
                                    effectiveMethod === "credits"
                                        ? "border-[#0F1E3D] bg-slate-50 ring-2 ring-[#0F1E3D]/10"
                                        : "border-slate-200 hover:border-slate-400"
                                }`}
                            >
                                <p className="text-sm font-medium text-slate-900">Use credits</p>
                                <p className="text-xs text-slate-500 mt-0.5">Debit <span className="font-mono">${chargeUsd.toFixed(2)}</span> from balance</p>
                                {effectiveMethod === "credits" && (
                                    <p className="text-xs text-emerald-700 mt-1">✓ Sends immediately — no checkout</p>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => setPayWith("card")}
                                data-testid="paywith-card"
                                className={`text-left border rounded-md p-3 transition-colors ${
                                    effectiveMethod === "card"
                                        ? "border-[#0F1E3D] bg-slate-50 ring-2 ring-[#0F1E3D]/10"
                                        : "border-slate-200 hover:border-slate-400"
                                }`}
                            >
                                <p className="text-sm font-medium text-slate-900">Pay with card</p>
                                <p className="text-xs text-slate-500 mt-0.5">One-time <span className="font-mono">${chargeUsd.toFixed(2)}</span> via Stripe</p>
                                {effectiveMethod === "card" && (
                                    <p className="text-xs text-slate-600 mt-1">Preserves your credit balance</p>
                                )}
                            </button>
                        </div>
                    </div>
                )}
                {!canUseCredits && balanceCents > 0 && (
                    <div className="flex items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-xs text-slate-700" data-testid="mail-credit-balance">
                        <span>Credits balance: <strong className="font-mono">${(balanceCents / 100).toFixed(2)}</strong></span>
                        <span className="text-slate-500">Not enough — will pay with card</span>
                    </div>
                )}

                <div className="grid md:grid-cols-2 gap-3">
                    <AddressBlock label="From (return address)" addr={caseData?.debtor_address} name={userName || "Client"} />
                    <AddressBlock label="To (recipient)" addr={caseData?.creditor_address} name={caseData?.creditor_name || "Recipient"} />
                </div>

                <div className="space-y-2">
                    <Label htmlFor="postage-select">Postage class</Label>
                    <Select value={postage} onValueChange={setPostage}>
                        <SelectTrigger id="postage-select" data-testid="postage-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {options.map((o) => (
                                <SelectItem key={o.key} value={o.key} data-testid={`postage-opt-${o.key}`}>
                                    {o.label} — ${(o.charge_usd ?? o.estimate_usd).toFixed(2)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {currentOpt && (
                        <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2.5 space-y-0.5" data-testid="mail-cost-breakdown">
                            <div className="flex justify-between">
                                <span>USPS postage &amp; Lob printing</span>
                                <span className="font-mono">${currentOpt.estimate_usd.toFixed(2)}</span>
                            </div>
                            {markup > 0 && (
                                <div className="flex justify-between">
                                    <span>Processing</span>
                                    <span className="font-mono">${markup.toFixed(2)}</span>
                                </div>
                            )}
                            <div className="flex justify-between font-semibold text-slate-900 pt-1 border-t border-slate-200">
                                <span>Total charged today</span>
                                <span className="font-mono" data-testid="mail-total-charge">${(currentOpt.charge_usd ?? currentOpt.estimate_usd).toFixed(2)}</span>
                            </div>
                            {currentOpt.extra_service && (
                                <p className="text-slate-500 mt-1 flex items-center gap-1">
                                    <MapPin size={11} /> Includes USPS tracking + delivery proof
                                </p>
                            )}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>Cancel</Button>
                    <Button onClick={send} disabled={sending} data-testid="mail-letter-send">
                        {sending ? "Processing…" : effectiveMethod === "credits"
                            ? `Use credits & mail — $${chargeUsd.toFixed(2)}`
                            : `Pay & Mail — $${chargeUsd.toFixed(2)}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
